import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { serverFiles } from "utils/utils";
import NO_IMAGE from "images/NO_IMAGE.jpg";

export const splitFeedMedia = (medias = []) => {
  const visual = [];
  const files = [];
  medias.forEach((m, idx) => {
    const path =
      typeof m === "string" ? m : m?.path || m?.url || m?.mediaUrl;
    if (!path || typeof path !== "string") return;
    const type = String(m?.mediaType || m?.type || "").toUpperCase();
    const isVideo =
      type.includes("VIDEO") || /\.(mp4|webm|mov)(\?|$)/i.test(path);
    const isFile =
      type.includes("DOC") ||
      type.includes("PDF") ||
      type.includes("FILE") ||
      /\.(pdf|doc|docx)(\?|$)/i.test(path);
    const item = {
      key: m?.id ?? idx,
      path,
      url: serverFiles(path),
      isVideo,
      name: m?.name,
    };
    if (isFile) files.push(item);
    else visual.push(item);
  });
  return { visual, files };
};

const FeedMediaCarousel = ({ items = [] }) => {
  const trackRef = useRef(null);
  const videoRefs = useRef([]);
  const [index, setIndex] = useState(0);
  const count = items.length;
  const multi = count > 1;

  const goTo = (next) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(count - 1, next));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    setIndex((prev) => (prev === next ? prev : next));
  };

  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i !== index) {
        video.pause();
      }
    });
  }, [index]);

  if (!count) return null;

  return (
    <div className="relative rounded-xl overflow-hidden border border-zinc-800 bg-black">
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="feed-media-track flex overflow-x-auto snap-x snap-mandatory"
      >
        {items.map((item, i) => (
          <div
            key={item.key}
            className="min-w-full snap-center shrink-0 flex items-center justify-center h-[min(72vw,28rem)] bg-black"
          >
            {item.isVideo ? (
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                src={item.url}
                controls
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={item.url}
                alt=""
                loading="lazy"
                draggable={false}
                className="w-full h-full object-contain select-none"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = NO_IMAGE;
                }}
              />
            )}
          </div>
        ))}
      </div>

      {multi ? (
        <>
          {index > 0 ? (
            <button
              type="button"
              aria-label="Previous image"
              onClick={() => goTo(index - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-black/55 backdrop-blur-sm text-white inline-flex items-center justify-center border border-white/15 hover:bg-black/75 transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
          ) : null}
          {index < count - 1 ? (
            <button
              type="button"
              aria-label="Next image"
              onClick={() => goTo(index + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-black/55 backdrop-blur-sm text-white inline-flex items-center justify-center border border-white/15 hover:bg-black/75 transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          ) : null}

          <div className="absolute top-2.5 right-2.5 rounded-full bg-black/60 backdrop-blur-sm text-white text-[0.65rem] font-semibold tracking-wide px-2 py-0.5">
            {index + 1} / {count}
          </div>

          <div className="absolute bottom-2.5 left-0 right-0 flex items-center justify-center gap-1.5">
            {items.map((item, i) => (
              <button
                key={`dot-${item.key}`}
                type="button"
                aria-label={`Go to image ${i + 1}`}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index
                    ? "w-4 bg-[#6C63F5]"
                    : "w-1.5 bg-white/45 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FeedMediaCarousel;
